import { useCallback, useRef } from 'react';
import { useQueryClient, QueryKey } from '@tanstack/react-query';
import {
  useGetBobcatDataForTicketsHook,
  IssuedTicketBobcatDataMapDTO,
  IssuedTicketFindResultDTO,
} from '@/api/trafikportalen';
import { applyBobcatDataToTickets } from '@/helpers/issuedTicketQueries';
import { useTicketBobcatDataStore } from '@/stores/ticketBobcatData';

const MAX_TICKET_IDS_PER_REQUEST = 50;

interface UseVisibleTicketsUpdaterParams {
  queryKey: QueryKey;
}

export const useVisibleTicketsUpdater = ({
  queryKey,
}: UseVisibleTicketsUpdaterParams) => {
  const queryClient = useQueryClient();
  const getBobcatDataForTickets = useGetBobcatDataForTicketsHook();
  const setBobcatData = useTicketBobcatDataStore((state) => state.setMany);
  const fetchedTicketsRef = useRef<Set<string>>(new Set());

  const updateVisibleTickets = useCallback(
    async (visibleTickets: IssuedTicketFindResultDTO[]) => {
      if (!visibleTickets.length) return;

      const idsToFetch = visibleTickets
        .filter(
          (ticket) =>
            (ticket.state === 'active' || ticket.state === 'inactive') &&
            !fetchedTicketsRef.current.has(ticket.id),
        )
        .map((ticket) => ticket.id);

      if (!idsToFetch.length) return;

      idsToFetch.forEach((id) => fetchedTicketsRef.current.add(id));

      const idChunks: string[][] = [];
      for (let i = 0; i < idsToFetch.length; i += MAX_TICKET_IDS_PER_REQUEST) {
        idChunks.push(idsToFetch.slice(i, i + MAX_TICKET_IDS_PER_REQUEST));
      }

      try {
        const responses = await Promise.all(
          idChunks.map((ids) =>
            getBobcatDataForTickets({ ids: ids.join(',') }),
          ),
        );

        const bobcatData = responses.reduce<IssuedTicketBobcatDataMapDTO>(
          (acc, response) => ({ ...acc, ...response }),
          {},
        );

        if (Object.keys(bobcatData).length === 0) return;

        setBobcatData(bobcatData);

        queryClient.setQueryData<IssuedTicketFindResultDTO[]>(
          queryKey,
          (oldData) =>
            oldData ? applyBobcatDataToTickets(oldData, bobcatData) : oldData,
        );
      } catch (error) {
        idsToFetch.forEach((id) => fetchedTicketsRef.current.delete(id));
        console.error('Failed to fetch updated ticket data:', error);
      }
    },
    [queryClient, getBobcatDataForTickets, setBobcatData, queryKey],
  );

  return updateVisibleTickets;
};
